import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Mail,
  Send,
  Copy,
  Eye,
  Code,
  Loader2,
  ArrowLeft,
  Sparkles,
  CheckCircle2,
  Trash2,
  Smartphone,
  Monitor,
  User,
  Building2,
  Briefcase,
  Target,
  MessageSquareQuote,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../../firebase.init";

const API_BASE = "https://emailserverjuly.shahlimon.online";

const EmailGenerator = () => {
  const [formData, setFormData] = useState({
    recipientName: "",
    companyName: "",
    industry: "",
    purpose: "Cold Outreach",
    tone: "Professional",
    customOffer: "",
  });

  const [user] = useAuthState(auth);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [viewMode, setViewMode] = useState("preview"); // 'preview' or 'code'
  const [previewDevice, setPreviewDevice] = useState("desktop"); // 'desktop' or 'mobile'
  const [copySuccess, setCopySuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!formData.recipientName || !formData.purpose) {
      toast.error("Recipient name and purpose are required.");
      return;
    }

    setIsGenerating(true);
    await new Promise((resolve) => setTimeout(resolve, 800));

    try {
      if (user?.email) {
        const geminiRes = await axios.get(`https://emailserverjuly.shahlimon.online/gemini-api-config?ownerEmail=${user.email}`).catch(() => ({ data: [] }));
        if (!geminiRes.data || geminiRes.data.length === 0 || !geminiRes.data[0].geminiApiKey) {
          toast.error("Please configure your Gemini API Key in Settings first.");
          setIsGenerating(false);
          return;
        }
      }
      const response = await axios.post(
        `${API_BASE}/api/generate-cold-email`,
        { ...formData, ownerEmail: user?.email }
      );
      if (response.data.success) {
        setGeneratedHtml(response.data.html);
        toast.success("Strategy synthesized successfully!");
      } else {
        toast.error(response.data.message || "Generation failed.");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error("AI Engine connectivity issue.");
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    toast.success(`${type} copied!`);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const resetForm = () => {
    setFormData({
      recipientName: "",
      companyName: "",
      industry: "",
      purpose: "Cold Outreach",
      tone: "Professional",
      customOffer: "",
    });
    setGeneratedHtml("");
  };

  return (
    <div className="generator-root">
      <div className="generator-container">
        {/* Top Navbar */}
        <header className="generator-header">
          <div className="header-left">
            <Link to="/admin/email-marketing" className="exit-button">
              <ArrowLeft size={16} />
              <span>Dashboard</span>
            </Link>
            <div className="header-divider" />
            <div className="header-logo">
              <div className="logo-icon">
                <Sparkles size={16} />
              </div>
              <h1 className="logo-text">
                AI Engine <span>/ Cold Outreach</span>
              </h1>
            </div>
          </div>

          <div className="header-actions">
            <button onClick={resetForm} className="reset-button">
              <Trash2 size={16} />
              <span>Reset</span>
            </button>
            <button
              className="generate-button-small"
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Zap size={16} />
              )}
              <span>{isGenerating ? "Synthesizing..." : "Generate"}</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="generator-main">
          {/* Left Panel: Form */}
          <aside className="input-panel">
            <div className="input-panel-inner">
              <div className="panel-header">
                <span className="panel-tag">Configuration</span>
                <h2 className="panel-title">Refine Strategy</h2>
                <p className="panel-desc">
                  Set the context for your personalized message.
                </p>
              </div>

              <form onSubmit={handleGenerate} className="generator-form">
                <div className="form-group">
                  <label className="field-label">
                    <User size={12} /> Target Full Name
                  </label>
                  <input
                    type="text"
                    name="recipientName"
                    value={formData.recipientName}
                    onChange={handleInputChange}
                    placeholder="e.g. John Doe"
                    className="custom-input"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="field-label">
                      <Building2 size={12} /> Company
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      placeholder="Acme Inc."
                      className="custom-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="field-label">
                      <Briefcase size={12} /> Industry
                    </label>
                    <input
                      type="text"
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      placeholder="SaaS / AI"
                      className="custom-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="field-label">
                    <Target size={12} /> Email Objective
                  </label>
                  <div className="options-grid">
                    {[
                      "Cold Outreach",
                      "Follow Up",
                      "Meeting",
                      "Partnership",
                    ].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, purpose: type }))
                        }
                        className={`option-btn ${formData.purpose === type ? "active" : ""}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="field-label">Tone Palette</label>
                  <div className="tone-chips">
                    {["Professional", "Bold", "Friendly", "Creative"].map(
                      (tone) => (
                        <button
                          key={tone}
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({ ...prev, tone }))
                          }
                          className={`tone-chip ${formData.tone === tone ? "active" : ""}`}
                        >
                          {tone}
                        </button>
                      ),
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="field-label">
                    <MessageSquareQuote size={12} /> Custom Context
                  </label>
                  <textarea
                    name="customOffer"
                    value={formData.customOffer}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Add specific details or offers..."
                    className="custom-textarea"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isGenerating}
                  className="main-generate-btn"
                >
                  {isGenerating ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <Sparkles size={20} />
                  )}
                  <span>
                    {isGenerating
                      ? "AI Engine Working..."
                      : "Generate Outreach"}
                  </span>
                </button>
              </form>
            </div>
          </aside>

          {/* Right Panel: Preview */}
          <section className="preview-panel">
            <div className="preview-toolbar">
              <div className="view-toggle">
                <button
                  onClick={() => setViewMode("preview")}
                  className={`toggle-btn ${viewMode === "preview" ? "active" : ""}`}
                >
                  <Eye size={14} /> Preview
                </button>
                <button
                  onClick={() => setViewMode("code")}
                  className={`toggle-btn ${viewMode === "code" ? "active" : ""}`}
                >
                  <Code size={14} /> Source
                </button>
              </div>

              <div className="device-toggle">
                <button
                  onClick={() => setPreviewDevice("desktop")}
                  className={`device-btn ${previewDevice === "desktop" ? "active" : ""}`}
                >
                  <Monitor size={16} />
                </button>
                <button
                  onClick={() => setPreviewDevice("mobile")}
                  className={`device-btn ${previewDevice === "mobile" ? "active" : ""}`}
                >
                  <Smartphone size={16} />
                </button>
              </div>

              <div className="toolbar-actions">
                <button
                  onClick={() => copyToClipboard(generatedHtml, "HTML")}
                  disabled={!generatedHtml}
                  className="copy-button"
                >
                  <Copy size={16} />
                  <span>Copy HTML</span>
                </button>
              </div>
            </div>

            <div className="preview-content">
              <AnimatePresence mode="wait">
                {!generatedHtml && !isGenerating ? (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="empty-state"
                  >
                    <div className="empty-icon">
                      <Mail size={40} />
                    </div>
                    <h3>Synthesizer Ready</h3>
                    <p>
                      Provide details on the left to generate your outreach
                      strategy.
                    </p>
                  </motion.div>
                ) : isGenerating ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="loading-state"
                  >
                    <div className="loader-container">
                      <div className="pulsing-circle" />
                      <Sparkles size={30} className="loader-sparkle" />
                    </div>
                    <p>AI Engine is analyzing context...</p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`preview-wrapper ${previewDevice}`}
                  >
                    {viewMode === "preview" ? (
                      <div className="email-card">
                        <div className="email-card-header">
                          <div className="email-info">
                            <div className="email-avatar">
                              {formData.recipientName?.charAt(0) || "T"}
                            </div>
                            <div>
                              <div className="email-name">
                                {formData.recipientName || "Target Recipient"}
                              </div>
                              <div className="email-meta">
                                To: {formData.companyName || "Company"} • Just
                                now
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          className="email-body"
                          dangerouslySetInnerHTML={{ __html: generatedHtml }}
                        />
                      </div>
                    ) : (
                      <div className="code-card">
                        <div className="code-header">
                          <span className="code-tag">HTML Editor</span>
                          <span className="code-hint">
                            Changes reflect in preview automatically
                          </span>
                        </div>
                        <textarea
                          className="html-editor"
                          value={generatedHtml}
                          onChange={(e) => setGeneratedHtml(e.target.value)}
                          placeholder="Paste or edit HTML here..."
                          spellCheck="false"
                        />
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </section>
        </main>
      </div>

      <style jsx="true">{`
        .generator-root {
          --bg-main: #020617;
          --bg-panel: #0a0f1d;
          --accent: #6366f1;
          --accent-hover: #4f46e5;
          --text-main: #f8fafc;
          --text-muted: #64748b;
          --border: rgba(255, 255, 255, 0.08);

          position: relative;
          background: var(--bg-main);
          color: var(--text-main);
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
            Arial, sans-serif;
          overflow: hidden;
          border-radius: 24px;
          border: 1px solid var(--border);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          height: calc(100vh - 120px);
          margin: 20px;
        }

        .generator-container {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .generator-header {
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 24px;
          border-bottom: 1px solid var(--border);
          background: rgba(10, 15, 29, 0.5);
          backdrop-filter: blur(12px);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .exit-button {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-muted);
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          padding: 8px 12px;
          border-radius: 8px;
          transition: all 0.2s;
        }

        .exit-button:hover {
          background: rgba(255, 255, 255, 0.05);
          color: white;
        }

        .header-divider {
          width: 1px;
          height: 24px;
          background: var(--border);
        }

        .header-logo {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .logo-icon {
          width: 32px;
          height: 32px;
          background: var(--accent);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }

        .logo-text {
          font-size: 18px !important;
          font-weight: 700 !important;
          margin: 0 !important;
          color: white !important;
        }

        .logo-text span {
          color: var(--text-muted);
          font-weight: 400;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .reset-button {
          background: none;
          border: none;
          color: var(--text-muted);
          font-size: 13px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          padding: 8px 12px;
        }

        .generate-button-small {
          background: var(--accent);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
        }

        .generator-main {
          flex: 1;
          display: flex;
          overflow: hidden;
        }

        .input-panel {
          width: 35%;
          max-width: 480px;
          min-width: 360px;
          border-right: 1px solid var(--border);
          overflow-y: auto;
          background: var(--bg-panel);
        }

        .input-panel-inner {
          padding: 32px;
        }

        .panel-header {
          margin-bottom: 32px;
        }

        .panel-tag {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--accent);
          display: block;
          margin-bottom: 8px;
        }

        .panel-title {
          font-size: 24px !important;
          font-weight: 700 !important;
          margin-bottom: 8px !important;
          color: white !important;
        }

        .panel-desc {
          font-size: 14px;
          color: var(--text-muted);
          line-height: 1.5;
        }

        .generator-form {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .field-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .custom-input {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 12px 16px;
          color: white;
          font-size: 14px;
          outline: none;
          transition: all 0.2s;
        }

        .custom-input:focus {
          border-color: var(--accent);
          background: rgba(255, 255, 255, 0.05);
        }

        .form-row {
          display: grid;
          grid-template-cols: 1fr 1fr;
          gap: 16px;
        }

        .options-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .option-btn {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 10px 14px;
          color: var(--text-muted);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .option-btn:hover {
          background: rgba(255, 255, 255, 0.08);
          color: white;
        }

        .option-btn.active {
          background: rgba(99, 102, 241, 0.15);
          border-color: var(--accent);
          color: #818cf8;
          box-shadow: 0 0 0 1px var(--accent);
        }

        .tone-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .tone-chip {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--border);
          border-radius: 20px;
          padding: 6px 14px;
          color: var(--text-muted);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .tone-chip:hover {
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }

        .tone-chip.active {
          background: white;
          color: black;
          border-color: white;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
        }

        .custom-textarea {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 12px 16px;
          color: white;
          font-size: 14px;
          outline: none;
          resize: none;
        }

        .main-generate-btn {
          height: 56px;
          background: var(--accent);
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 16px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          cursor: pointer;
          box-shadow: 0 8px 24px rgba(99, 102, 241, 0.25);
          transition:
            transform 0.1s,
            opacity 0.2s;
        }

        .main-generate-btn:active {
          transform: scale(0.98);
        }

        .preview-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          background: #030712;
        }

        .preview-toolbar {
          min-height: 64px;
          height: auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 24px;
          border-bottom: 1px solid var(--border);
          flex-wrap: wrap;
          gap: 16px;
        }

        .view-toggle {
          display: flex;
          background: #111827;
          padding: 4px;
          border-radius: 10px;
        }

        .toggle-btn {
          background: none;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          color: var(--text-muted);
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
        }

        .toggle-btn.active {
          background: var(--accent);
          color: white;
        }

        .device-toggle {
          display: flex;
          gap: 8px;
        }

        .device-btn {
          background: #111827;
          border: 1px solid var(--border);
          color: var(--text-muted);
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .device-btn.active {
          color: var(--accent);
          border-color: var(--accent);
        }

        .copy-button {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid var(--border);
          color: white;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .preview-content {
          flex: 1;
          padding: 40px 24px;
          overflow-y: auto;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
          align-items: center;
          background-image: radial-gradient(#ffffff05 1px, transparent 1px);
          background-size: 20px 20px;
          scroll-behavior: smooth;
        }

        /* Custom Scrollbar */
        .preview-content::-webkit-scrollbar {
          width: 8px;
        }
        .preview-content::-webkit-scrollbar-track {
          background: transparent;
        }
        .preview-content::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        .preview-content::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
          background-clip: padding-box;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          max-width: 320px;
          margin: auto;
          padding: 40px 0;
        }

        .empty-icon {
          width: 80px;
          height: 80px;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--accent);
          margin-bottom: 24px;
        }

        .empty-state h3 {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 8px;
          color: #ffffff;
        }

        .empty-state p {
          color: var(--text-muted);
          font-size: 14px;
          line-height: 1.6;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          margin: auto;
          padding: 40px 0;
        }

        .loader-container {
          position: relative;
          width: 64px;
          height: 64px;
          margin-bottom: 24px;
        }

        .pulsing-circle {
          position: absolute;
          inset: 0;
          border: 2px solid var(--accent);
          border-radius: 50%;
          animation: pulse 1.5s infinite;
        }

        .loader-sparkle {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: var(--accent);
        }

        @keyframes pulse {
          0% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.2;
          }
          100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
        }

        .preview-wrapper {
          width: 100%;
          max-width: 700px;
          display: flex;
          justify-content: center;
        }

        .preview-wrapper.mobile {
          max-width: 375px;
        }

        .email-card {
          width: 100%;
          background: white;
          border-radius: 24px;
          box-shadow: 0 40px 80px rgba(0, 0, 0, 0.4);
          overflow: hidden;
          color: #1e293b;
          margin-bottom: 40px;
        }

        .email-card-header {
          padding: 24px;
          background: #f8fafc;
          border-bottom: 1px solid #f1f5f9;
        }

        .email-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .email-avatar {
          width: 40px;
          height: 40px;
          background: #e2e8f0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          color: #64748b;
        }

        .email-name {
          font-size: 14px;
          font-weight: 700;
        }

        .email-meta {
          font-size: 11px;
          color: #94a3b8;
        }

        .email-body {
          padding: 32px;
          line-height: 1.6;
          overflow-x: auto;
          word-break: break-word;
        }

        .email-body img {
          max-width: 100% !important;
          height: auto !important;
          display: block;
          margin: 16px 0;
        }

        .email-body table {
          width: 100% !important;
          max-width: 100% !important;
          border-collapse: collapse !important;
          margin: 16px 0;
        }

        .email-body a {
          color: var(--accent);
          word-break: break-all;
        }

        .email-body p {
          margin-bottom: 1em;
        }

        .code-card {
          width: 100%;
          background: #0f172a;
          border: 1px solid var(--border);
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          margin-bottom: 40px;
          height: 600px;
        }

        .code-header {
          padding: 12px 20px;
          background: rgba(255, 255, 255, 0.02);
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .code-tag {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent);
        }

        .code-hint {
          font-size: 10px;
          color: var(--text-muted);
        }
        .html-editor {
          flex: 1;
          background: transparent;
          border: none;
          color: #94a3b8;
          padding: 20px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace;
          font-size: 13px;
          line-height: 1.6;
          resize: none;
          outline: none;
          width: 100%;
          overflow: auto !important;
          white-space: pre;
          tab-size: 2;
          display: block;
        }

        /* Editor Scrollbar */
        .html-editor::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .html-editor::-webkit-scrollbar-track {
          background: transparent;
        }
        .html-editor::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }
        .html-editor::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .html-editor:focus {
          color: #f8fafc;
        }
        @media (max-width: 1024px) {
          .generator-main {
            flex-direction: column;
            overflow-y: auto;
          }
          .input-panel {
            width: 100%;
            max-width: none;
            height: auto;
            border-right: none;
            border-bottom: 1px solid var(--border);
            overflow-y: visible;
          }
          .preview-panel {
            flex: none;
            min-height: 600px;
          }
          .preview-content {
            padding: 32px 16px;
          }
        }

        @media (max-width: 640px) {
          .generator-header {
            padding: 0 16px;
          }
          .header-left span,
          .header-actions span {
            display: none;
          }
          .input-panel-inner {
            padding: 20px;
          }
          .form-row {
            grid-template-cols: 1fr;
          }
          .preview-toolbar {
            padding: 12px 16px;
            justify-content: center;
          }
          .email-body {
            padding: 20px;
          }
          .logo-text {
            font-size: 14px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default EmailGenerator;
